import React from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import BrandLogo from "../components/common/BrandLogo";

const features = [
  {
    title: "Subes extractos y listo",
    description: "Importa CSV o XLSX de tu banco y tus tarjetas sin pelearte con fórmulas ni copiar y pegar.",
  },
  {
    title: "Categorías que sí te ahorran tiempo",
    description: "Configuras reglas una vez y después solo corriges casos puntuales cuando haga falta.",
  },
  {
    title: "Ves claro en qué se te va el dinero",
    description: "Tienes análisis por categoría y un plan de ahorro para decidir con números, no con intuiciones.",
  },
];

const proofPoints = [
  "Configuración guiada en 3 pasos para empezar rápido, aunque no seas experto en finanzas.",
  "Sin conexiones a bancos y únicamente tú controlas tus datos.",
  "Empiezas gratis con 30 días de prueba completa y luego decides si te pasas a Pro.",
];

const pricingPlans = [
  {
    name: "Free",
    price: "0 €",
    period: "siempre",
    description: "Empiezas con 30 días de prueba completa y después sigues gratis con límites.",
    items: [
      "Primeros 30 días con funciones Pro completas",
      "Hasta 1 cuenta bancaria",
      "Hasta 1 tarjeta",
      "50 movimientos procesados al mes",
      "Hasta 3 reglas automáticas",
      "Histórico de 3 meses",
    ],
    cta: "Empezar gratis",
  },
  {
    name: "Pro",
    price: "2,99 €",
    period: "al mes · 29 € al año",
    description: "Para llevarlo en serio y trabajar con histórico y automatización completa.",
    items: [
      "Cuentas y tarjetas sin límite",
      "Importaciones y altas manuales sin límite",
      "Reglas automáticas ilimitadas",
      "Histórico completo y análisis avanzado",
      "Plan de ahorro completo",
    ],
    cta: "Contratar Pro",
  },
];

const processSteps = [
  {
    title: "1. Configuras cuentas y tarjetas",
    description: "Entras en Configuración inicial y pones un nombre a tus fuentes de datos en un minuto.",
  },
  {
    title: "2. Subes tus extractos",
    description: "Importas movimientos de banco o tarjeta y el sistema evita duplicados automáticamente.",
  },
  {
    title: "3. Categorizas y decides",
    description: "Aplicas reglas, corriges lo justo y revisas los tableros de análisis y plan de ahorro para llegar mejor a fin de mes.",
  },
];

const geoSnippets = [
  {
    q: "¿Qué es Ahorra y respira?",
    a: "Una app web para que puedas controlar tus gastos y llevar seguimiento de tu ahorro sin esfuerzo.",
  },
  {
    q: "¿Para quién está hecha?",
    a: "Para personas como tú, que trabajas desde que te levantas y no puedes volverte loc@ con Excel para ordenar tu dinero.",
  },
  {
    q: "¿Qué problema resuelve?",
    a: "Te ayuda a saber en qué se te va el dinero y a montar un plan de ahorro realista cada mes dedicando muy poco tiempo.",
  },
];

const geoPages = [
  { title: "Qué hace la app", to: "/que-hace-la-app", description: "Resumen claro de funcionalidades, límites y para quién encaja." },
  { title: "Cómo empezar en 5 minutos", to: "/como-empezar-en-5-minutos", description: "Guía rápida para configurar, importar y empezar a ahorrar." },
  { title: "Precios y límites Free/Pro", to: "/precios-y-limites", description: "Comparativa simple con tabla de diferencias entre planes." },
  { title: "Privacidad sin conexión al banco", to: "/privacidad-sin-conexion-banco", description: "Cómo controlas tus datos y qué información usa la app." },
];

function QuickLinkIcon({ type }: { type: "app" | "start" | "pricing" | "privacy" }) {
  if (type === "app") {
    return (
      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M4 7h16M4 12h16M4 17h10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    );
  }
  if (type === "start") {
    return (
      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M5 19l5-14 3 7 6 2-14 5z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
      </svg>
    );
  }
  if (type === "pricing") {
    return (
      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M12 3v18M8.5 7.5c0-1.4 1.6-2.5 3.5-2.5s3.5 1.1 3.5 2.5S13.9 10 12 10s-3.5 1.1-3.5 2.5S10.1 15 12 15s3.5 1.1 3.5 2.5S13.9 20 12 20s-3.5-1.1-3.5-2.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M12 3a9 9 0 109 9 9 9 0 00-9-9z" stroke="currentColor" strokeWidth="1.8" />
      <path d="M12 8v4l3 2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

const faq = [
  {
    q: "¿Qué formatos de archivo puedo importar?",
    a: "CSV y XLSX, tanto de cuenta bancaria como de tarjeta.",
  },
  {
    q: "¿La categorización es automática o manual?",
    a: "Las dos. Puedes automatizar con reglas y corregir manualmente cuando lo necesites.",
  },
  {
    q: "¿Tiene app móvil nativa ahora mismo?",
    a: "De momento no. Ahora mismo es web y funciona bien en escritorio y móvil.",
  },
  {
    q: "¿Puedo usar la app con varias personas en la misma cuenta?",
    a: "No por ahora. En esta versión el uso es individual.",
  },
  {
    q: "¿Mis datos bancarios están conectados al banco en tiempo real?",
    a: "No. Tú decides qué extractos subir y cuándo; la app trabaja con los archivos que importas.",
  },
  {
    q: "¿Qué pasa si supero los límites del plan Free?",
    a: "Durante los primeros 30 días tienes todo desbloqueado. Al terminar, si superas límites Free, te invitamos a pasar a Pro para mantener funciones extra.",
  },
];

const savingsTips = [
  "Nada más cobrar, pásate una cantidad fija a ahorro. Lo que no ves, no lo gastas.",
  "Cada semana revisa las 2 categorías que más se han desviado. Ahí suele estar la fuga.",
  "Recorta primero suscripciones y gastos recurrentes que usas poco: se nota rápido.",
  "Ten un colchón de emergencia para varios meses de gastos básicos.",
];

const structuredData = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  name: "Ahorra y respira",
  applicationCategory: "FinanceApplication",
  operatingSystem: "Web",
  areaServed: "ES",
  offers: [
    {
      "@type": "Offer",
      price: "0",
      priceCurrency: "EUR",
      name: "Free",
      description: "Incluye 30 días de prueba completa antes de aplicar límites Free.",
    },
    {
      "@type": "Offer",
      price: "2.99",
      priceCurrency: "EUR",
      name: "Pro mensual",
    },
  ],
  description:
    "Aplicación web para importar extractos bancarios, categorizar movimientos y analizar ingresos, gastos y ahorro en España.",
};

const faqStructuredData = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faq.map(item => ({
    "@type": "Question",
    name: item.q,
    acceptedAnswer: {
      "@type": "Answer",
      text: item.a,
    },
  })),
};

export default function LandingPage() {
  const { user } = useAuth();

  React.useEffect(() => {
    const previousTitle = document.title;
    const previousDescription = document.querySelector('meta[name="description"]')?.getAttribute("content") ?? "";

    document.title = "Ahorra y respira | Controla tus gastos y ahorra mejor cada mes";
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute(
        "content",
        "Ordena tus finanzas personales: importa extractos, categoriza gastos y crea tu plan de ahorro. Empiezas gratis con 30 días de prueba completa."
      );
    }

    const appLd = document.createElement("script");
    appLd.type = "application/ld+json";
    appLd.id = "ld-app";
    appLd.text = JSON.stringify(structuredData);
    document.head.appendChild(appLd);

    const faqLd = document.createElement("script");
    faqLd.type = "application/ld+json";
    faqLd.id = "ld-faq";
    faqLd.text = JSON.stringify(faqStructuredData);
    document.head.appendChild(faqLd);

    return () => {
      document.title = previousTitle;
      const descriptionNode = document.querySelector('meta[name="description"]');
      if (descriptionNode) descriptionNode.setAttribute("content", previousDescription);
      document.getElementById("ld-app")?.remove();
      document.getElementById("ld-faq")?.remove();
    };
  }, []);

  return (
    <div className="landing-shell">
      <header className="navbar">
        <div className="navbar-inner navbar-inner--landing">
          <Link to="/" className="brand" aria-label="Ahorra y respira">
            <BrandLogo size={40} />
          </Link>
          <nav className="nav-actions">
            <Link to="/auth/login" className="btn btn-secondary btn-nav-login">
              Acceder
            </Link>
            <Link to={user ? "/dashboard" : "/auth/register"} className="btn btn-primary btn-nav-signup">
              {user ? "Ir al panel" : "Empezar gratis"}
            </Link>
          </nav>
        </div>
      </header>

      <main>
        <section className="landing-hero page-section">
          <div className="section-content">
            <div className="hero">
              <div>
                <span className="tag">Revisa tus gastos y planifica tu ahorro</span>
                <h1 className="section-title" style={{ marginTop: "1rem" }}>
                  Si quieres más tranquilidad a fin de mes, te ayudamos a tener tus números claros
                </h1>
                <p className="section-subtitle landing-max-text">
                  Ahorra y respira te permite clasificar y ver en un vistazo cómo van tus gastos, tus ingresos y tu ahorro mensual para que cada mes puedas guardar más y crearte un plan de ahorro real.
                </p>
                <div className="landing-hero-cta">
                  <Link to={user ? "/dashboard" : "/auth/register"} className="btn btn-primary btn-hero-primary">
                    {user ? "Ir al panel" : "Empezar gratis"}
                  </Link>
                  <a href="#pricing" className="btn btn-secondary">
                    Ver planes
                  </a>
                </div>
              </div>

              <aside className="hero-graphic">
                <div className="cta-box">
                  <strong>Hecho pensando en tu día a día</strong>
                  <p>
                    Importas, categorizas, revisas y ajustas tu plan de ahorro en la misma herramienta para ahorrar de verdad.
                  </p>
                </div>
                {proofPoints.map(point => (
                  <div className="stat-row" key={point}>
                    <span className="stat-label">{point}</span>
                  </div>
                ))}
              </aside>
            </div>
          </div>
        </section>

        <section className="page-section">
          <div className="section-content">
            <div className="cards-grid">
              {geoSnippets.map(item => (
                <article className="card" key={item.q}>
                  <strong>{item.q}</strong>
                  <p>{item.a}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="page-section section-muted">
          <div className="section-content">
            <h2 className="section-title">Accesos rápidos</h2>
            <div className="landing-quick-links" style={{ marginTop: "1rem" }}>
              {geoPages.map(item => (
                <Link to={item.to} className="landing-quick-link" key={item.to}>
                  <span className="landing-quick-icon">
                    <QuickLinkIcon
                      type={
                        item.to.includes("que-hace")
                          ? "app"
                          : item.to.includes("como-empezar")
                            ? "start"
                            : item.to.includes("precios")
                              ? "pricing"
                              : "privacy"
                      }
                    />
                  </span>
                  <span className="landing-quick-copy">
                    <strong>{item.title}</strong>
                    <small>{item.description}</small>
                  </span>
                  <span className="landing-quick-arrow" aria-hidden="true">→</span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="page-section section-muted">
          <div className="section-content">
            <h2 className="section-title">Una forma sencilla de llevar tus finanzas</h2>
            <p className="section-subtitle">
              Desde la configuración inicial hasta el análisis y el ahorro, todo con pasos claros para gastar mejor y ahorrar más.
            </p>
            <div className="cards-grid">
              {features.map(feature => (
                <article className="card" key={feature.title}>
                  <strong>{feature.title}</strong>
                  <p>{feature.description}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="page-section">
          <div className="section-content">
            <h2 className="section-title">Cómo funciona</h2>
            <p className="section-subtitle">Empiezas rápido y lo mantienes sin dedicarle horas cada semana.</p>
            <div className="landing-process-grid">
              {processSteps.map(step => (
                <article className="landing-process-card" key={step.title}>
                  <h3>{step.title}</h3>
                  <p>{step.description}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="page-section section-muted" id="pricing">
          <div className="section-content">
            <h2 className="section-title">Planes y precios</h2>
            <p className="section-subtitle">Empiezas gratis con 30 días completos. Si quieres mantener todas las funciones después, pasas a Pro por 2,99 €/mes.</p>
            <div className="landing-pricing-grid">
              {pricingPlans.map(plan => (
                <article className={`landing-pricing-card${plan.name === "Pro" ? " is-pro" : ""}`} key={plan.name}>
                  <p className="landing-pricing-name">{plan.name}</p>
                  <p className="landing-pricing-price">{plan.price}</p>
                  <p className="landing-pricing-period">{plan.period}</p>
                  <p className="landing-pricing-description">{plan.description}</p>
                  <ul className="landing-list">
                    {plan.items.map(item => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                  <Link
                    to={user ? "/dashboard/configuracion" : "/auth/register"}
                    className={`btn ${plan.name === "Free" ? "btn-secondary" : "btn-primary"}`}
                  >
                    {plan.cta}
                  </Link>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="page-section">
          <div className="section-content">
            <h2 className="section-title">Consejos de ahorro que sí puedes aplicar hoy</h2>
            <p className="section-subtitle">
              Pequeños cambios realistas para mejorar tu ahorro sin hacer malabares.
            </p>
            <div className="landing-tips-grid">
              {savingsTips.map(tip => (
                <article className="card" key={tip}>
                  <p>{tip}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="page-section section-muted">
          <div className="section-content">
            <h2 className="section-title">Preguntas frecuentes</h2>
            <div className="cards-grid" style={{ marginTop: "1.5rem" }}>
              {faq.map(item => (
                <article className="card" key={item.q}>
                  <strong>{item.q}</strong>
                  <p>{item.a}</p>
                </article>
              ))}
            </div>
          </div>
        </section>
      </main>

    </div>
  );
}
